import { useEffect, useRef, useState } from "react";
import { channelsApi } from "@/lib/api";
import { pairingUserMessage } from "@/lib/pairingErrors";

const POLL_MS = 2000;

export function mergePairingPayload(base, update) {
  if (!update) return base;
  const next = { ...base };
  for (const key of ["qr_payload", "qr_code_url", "pairing_code", "expires_at", "pairing_attempt_id", "client_session_id", "login_session_id"]) {
    if (update[key]) next[key] = update[key];
  }
  return next;
}

function resolvePollError(e) {
  const msg = pairingUserMessage(e, "Couldn't reach the gateway");
  if (e?.status === 0 || /failed to fetch/i.test(msg)) {
    return "Can't reach OpenClaw — check your network and that the gateway is running, then try again.";
  }
  if (e?.code === "openclaw_schema_mismatch") {
    return "OpenClaw gateway version mismatch — contact your admin.";
  }
  return msg;
}

function isMockLabel(label) {
  if (!label) return false;
  return /\(mock\)/i.test(label) || /mock\.openclaw/i.test(label);
}

function applyPairedStatus(r, { onPaired, pairedFiredRef, setters, attemptId, responseAttempt }) {
  const { setPollStatus, setPhoneNumber, setAccountName, setSupersededMessage } = setters;

  // Pairing completion only — ignore manage/already-connected payloads that
  // reuse pair/status for connected WhatsApp rows.
  if (r?.mode === "manage" || r?.already_connected) {
    setPollStatus("pending");
    return;
  }
  if (attemptId && responseAttempt && responseAttempt !== attemptId) {
    setPollStatus("paired_elsewhere");
    setSupersededMessage(
      "WhatsApp was connected by a different browser session. This QR is no longer valid.",
    );
    return;
  }
  if (r?.owned_by_this_session === false) {
    setPollStatus("paired_elsewhere");
    setSupersededMessage(
      r.message || "WhatsApp was connected by a different browser session.",
    );
    return;
  }
  const phone = r.phone_number || r.device_label;
  if (isMockLabel(phone) || isMockLabel(r.device_label)) {
    setPollStatus("pending");
    return;
  }
  setPollStatus("paired");
  setPhoneNumber(phone || null);
  setAccountName(r.account_name || null);
  if (!pairedFiredRef.current) {
    pairedFiredRef.current = true;
    onPaired?.(phone || r.device_label || "Connected");
  }
}

function applyPairStatusResult(r, { onPaired, pairedFiredRef, setters, attemptId }) {
  const {
    setPollError,
    setLivePairing,
    setQrImageError,
    setPollStatus,
    setSupersededMessage,
  } = setters;

  setPollError(null);

  if (r?.status === "superseded" || r?.status === "paired_elsewhere") {
    setPollStatus(r.status);
    setSupersededMessage(r.message || (
      r.status === "paired_elsewhere"
        ? "WhatsApp was connected in another browser session. This QR is no longer valid."
        : "Another browser started a newer pairing session. Refresh to get a new QR."
    ));
    // Do not adopt another session's QR into this browser's modal.
    return;
  }

  // Only merge QR updates for this browser's attempt.
  const responseAttempt = r?.pairing_attempt_id;
  const ownsSession = !attemptId || !responseAttempt || responseAttempt === attemptId;
  if (ownsSession && (r?.qr_payload || r?.qr_code_url || r?.expires_at)) {
    setLivePairing((prev) => mergePairingPayload(prev || {}, r));
    setQrImageError(false);
  }

  if (r?.status === "paired") {
    applyPairedStatus(r, {
      onPaired,
      pairedFiredRef,
      setters,
      attemptId,
      responseAttempt,
    });
    return;
  }

  if (r?.status === "expired") {
    setPollStatus("expired");
    return;
  }

  setPollStatus("pending");
  setSupersededMessage(null);
}

export function useChannelPairingPoll({ open, connection, pairing, onPaired }) {
  const [pollStatus, setPollStatus] = useState("pending");
  const [pollError, setPollError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [accountName, setAccountName] = useState(null);
  const [livePairing, setLivePairing] = useState(pairing);
  const [qrImageError, setQrImageError] = useState(false);
  const [supersededMessage, setSupersededMessage] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const pairedFiredRef = useRef(false);
  const connRef = useRef(connection);
  const onPairedRef = useRef(onPaired);
  const attemptId = pairing?.pairing_attempt_id || null;

  useEffect(() => { connRef.current = connection; }, [connection]);
  useEffect(() => { onPairedRef.current = onPaired; }, [onPaired]);

  useEffect(() => {
    if (!open) return;
    setPollStatus("pending");
    setPollError(null);
    setPhoneNumber(null);
    setAccountName(null);
    setLivePairing(pairing);
    setQrImageError(false);
    setSupersededMessage(null);
    setNow(Date.now());
    pairedFiredRef.current = false;
  }, [open, pairing?.expires_at, pairing?.qr_payload, pairing?.pairing_attempt_id, connection?.id]);

  useEffect(() => {
    if (pairing) setLivePairing((prev) => mergePairingPayload(pairing, prev));
  }, [pairing]);

  useEffect(() => {
    if (!open || !connection?.id) return undefined;
    let cancelled = false;

    const setters = {
      setPollError,
      setLivePairing,
      setQrImageError,
      setPollStatus,
      setPhoneNumber,
      setAccountName,
      setSupersededMessage,
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await channelsApi.pairStatus(connRef.current.id, {
          background: true,
          attemptId: attemptId || undefined,
        });
        if (cancelled) return;
        applyPairStatusResult(r, {
          onPaired: onPairedRef.current,
          pairedFiredRef,
          setters,
          attemptId,
        });
      } catch (e) {
        if (cancelled) return;
        setPollError(resolvePollError(e));
      }
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    const clk = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
      clearInterval(clk);
    };
  }, [open, connection?.id, attemptId]);

  const expiresAt = livePairing?.expires_at ? Date.parse(livePairing.expires_at) : null;
  const remainingMs = expiresAt ? expiresAt - now : null;
  const locallyExpired = remainingMs != null && remainingMs <= 0;
  const effectiveStatus = locallyExpired && pollStatus !== "paired" ? "expired" : pollStatus;

  return {
    pollStatus: effectiveStatus,
    pollError,
    phoneNumber,
    accountName,
    livePairing,
    qrImageError,
    remainingMs,
    supersededMessage,
    setQrImageError,
  };
}
